import { LeftOutlined, RightOutlined, UploadOutlined } from "@ant-design/icons";
import { faMinus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Carousel, Form, Input, message, Upload } from "antd";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import Router from "../..";
import {
  useUploadMissionByInfluencer,
  useUploadPreMissionByInfluencer,
} from "../../hooks/useCampaign";
import { useInfluencerPaymentHistory } from "../../hooks/usePayment";
import { formatCurrency, parseApiAmount } from "../../utils/amountFormat";
import { APPLIED_STATUS, POSTING_REVIEW } from "../../utils/campaignStatus";
import {
  influencerPaymentStatusClass,
  influencerPaymentStatusLabel,
} from "../../utils/influencerPaymentStatus";

// 첨부파일 파일명 추출
const getFileName = (url, fileIndex) => {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname.split("/").pop() || `파일 ${fileIndex + 1}`;
  } catch {
    return url.split("/").pop() || `파일 ${fileIndex + 1}`;
  }
};

// 첨부파일 렌더링
const renderAttachmentFiles = (attachmentUrl) => {
  const urls = attachmentUrl.split(",").map((u) => u.trim());
  return urls.map((url, index) => {
    const fileName = getFileName(url, index);
    return (
      <div
        key={index}
        className={`border border-[#e0e0e0] rounded-lg p-2.5 bg-white ${
          index < urls.length - 1 ? "mb-[5px]" : "mb-[22px]"
        }`}
      >
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3"
        >
          <div className="text-[20px]">📄</div>
          <div className="flex-1">
            <div className="text-[12px] text-[#424242] font-medium">
              {fileName}
            </div>
          </div>
        </a>
      </div>
    );
  });
};

// 미션 url 유효성 검사
const validateUrl = (url) => {
  const urlPattern = /^https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/;
  try {
    new URL(url);
    return urlPattern.test(url);
  } catch {
    return false;
  }
};

export const MyCampaignApplyDetail = ({
  selectedCampaign,
  appliedCampaigns,
}) => {
  const [isMissionEditMode, setIsMissionEditMode] = useState(false);
  const [tempMissionUrls, setTempMissionUrls] = useState([]);
  const [postingFiles, setPostingFiles] = useState([]);
  const [isPostingEditMode, setIsPostingEditMode] = useState(false);
  const [postingCarouselIndex, setPostingCarouselIndex] = useState(0);
  const [missionForm] = Form.useForm();
  const postingCarouselRef = useRef(null);

  const { mutateAsync: uploadMission } = useUploadMissionByInfluencer();
  const { mutateAsync: uploadPreMission, isPending: isPostingUploading } =
    useUploadPreMissionByInfluencer();

  const { data: paymentHistory } = useInfluencerPaymentHistory({
    campaignId: appliedCampaigns?.campaign_id,
    status: null,
    enabled:
      selectedCampaign?.seeding_type === "paid" &&
      !!appliedCampaigns?.campaign_id,
  });

  const handleAddUrl = () => {
    const url = missionForm.getFieldValue("mission_url");
    if (!url || !url.trim()) {
      message.error({ content: "URL을 입력해주세요." });
      return;
    }
    const formattedUrl = url.trim();
    if (!validateUrl(formattedUrl)) {
      message.error({
        content: "유효하지 않은 URL입니다. 올바른 URL을 입력해주세요.",
      });
      return;
    }
    setTempMissionUrls((prev) => [...prev, formattedUrl]);
    missionForm.resetFields();
  };

  const handleRemoveUrl = (selectedUrl) => {
    setTempMissionUrls((prev) => prev.filter((url) => url !== selectedUrl));
  };

  // 포스팅 파일 업로드
  const handlePostingFileUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (!isImage && !isVideo) {
      message.error("이미지 또는 비디오 파일만 업로드 가능합니다.");
      return Upload.LIST_IGNORE;
    }
    setPostingFiles((prev) => [...prev, file]);
    return false;
  };

  const handleRemovePostingFile = (file) => {
    setPostingFiles((prev) => prev.filter((f) => f.name !== file.name));
  };

  const handleMissionUpload = async () => {
    if (tempMissionUrls.length === 0) {
      message.error({ content: "최소 하나의 URL을 추가해주세요." });
      return;
    }
    const urls =
      tempMissionUrls.length === 1
        ? tempMissionUrls[0]
        : tempMissionUrls.join(",");
    const success = await uploadMission({
      id: appliedCampaigns.id,
      campaign_id: appliedCampaigns.campaign_id,
      mission_url: urls,
    });
    if (success) {
      setIsMissionEditMode(false);
      missionForm.resetFields();
      setTempMissionUrls([]);
    }
  };

  const handlePostingUpload = async () => {
    if (postingFiles.length === 0) {
      message.error({ content: "최소 하나의 파일을 업로드해주세요." });
      return;
    }

    const confirmInfo = postingFiles.map((file) => ({
      original_filename: file.name,
      content_type: file.type,
    }));
    const isSingleFile = postingFiles.length === 1;
    const success = await uploadPreMission({
      id: appliedCampaigns.id,
      campaign_id: appliedCampaigns.campaign_id,
      confirm_info: isSingleFile ? confirmInfo[0] : confirmInfo,
      confirm_file: isSingleFile ? postingFiles[0] : postingFiles,
    });
    if (success) {
      setIsPostingEditMode(false);
      setPostingFiles([]);
    }
  };

  if (!selectedCampaign) return null;
  if (!appliedCampaigns) return null;

  const statusInfo =
    APPLIED_STATUS[appliedCampaigns.status] ?? APPLIED_STATUS.default;
  const headerStatusInfo =
    selectedCampaign?.seeding_type === "paid" &&
    appliedCampaigns.status === "completed"
      ? { ...statusInfo, label: "정산 대기" }
      : statusInfo;
  const lineSettlementStatus = paymentHistory?.result?.[0]?.status;

  const payoutLine = paymentHistory?.result?.[0];
  const payoutCurrency = payoutLine?.currency;
  const payoutGross = parseApiAmount(payoutLine?.settlement_amount);
  const payoutTax = parseApiAmount(payoutLine?.tax_amount);
  const payoutNet = parseApiAmount(payoutLine?.net_amount);

  const confirmUrls =
    appliedCampaigns?.confirm_url
      ?.split(",")
      .map((url) => url.trim())
      .filter(Boolean) ?? [];
  const hasMultipleConfirmUrls = confirmUrls.length > 1;
  const isFirstPostingSlide = postingCarouselIndex === 0;
  const isLastPostingSlide = postingCarouselIndex === confirmUrls.length - 1;
  const postingReview =
    POSTING_REVIEW[appliedCampaigns.status] ?? POSTING_REVIEW.default;
  const shouldShowMissionSection =
    selectedCampaign?.seeding_type === "paid"
      ? ["confirmed", "completed", "paid"].includes(appliedCampaigns.status)
      : ["accepted", "completed", "paid"].includes(appliedCampaigns.status);

  return (
    <>
      <div className="flex justify-between items-center mb-6 bg-[#f8f9fa] px-6 py-4 rounded-lg">
        <div className="text-[15px]">
          신청일 :{" "}
          {dayjs(appliedCampaigns.created_at).format("YYYY년 MM월 DD일 HH:mm")}
        </div>
        <div
          className={`px-4 py-1.5 rounded-full text-[14px] ${headerStatusInfo.className}`}
        >
          {headerStatusInfo.label}
        </div>
      </div>
      {appliedCampaigns.status === "paid" &&
        paymentHistory?.result?.length > 0 && (
          <div className="mb-6">
            <h3 className="mb-3 text-[#424242]">정산 현황</h3>
            <div className="rounded-xl bg-[#F8F9FA] p-5 text-[14px] leading-[1.6] shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="mb-5 flex items-start justify-between gap-3">
                <span
                  className={`inline-flex rounded-full px-4 py-1.5 text-[13px] font-medium ${influencerPaymentStatusClass(
                    lineSettlementStatus
                  )}`}
                >
                  {influencerPaymentStatusLabel(lineSettlementStatus)}
                </span>
                <div>
                  <div className="text-[13px] text-end font-medium">
                    실 지급액
                  </div>
                  <div className="mt-1 text-[20px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
                    {formatCurrency(payoutNet, payoutCurrency)}
                  </div>
                </div>
              </div>
              <div className="border-t border-dashed border-[#d7dde5] pt-4">
                <div className="flex items-center justify-between gap-4 text-[12px]">
                  <span className="text-[#6b7280]">리워드</span>
                  <span className="tabular-nums font-medium text-[#374151]">
                    {formatCurrency(payoutGross, payoutCurrency)}
                  </span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-4 text-[12px]">
                  <span className="text-[#6b7280]">원천징수</span>
                  <span className="tabular-nums font-medium text-[#374151]">
                    {formatCurrency(payoutTax, payoutCurrency)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      <div className="mb-6">
        <h3 className="mb-3 text-[#424242]">지원 메시지</h3>
        <div className="whitespace-pre-line p-5 bg-white rounded-lg border border-[#e0e0e0] text-[14px] leading-[1.6]">
          {appliedCampaigns.message}
        </div>
      </div>
      {appliedCampaigns.attachment_url && (
        <div>
          <h3 className="mb-3 text-[#424242]">첨부파일</h3>
          {renderAttachmentFiles(appliedCampaigns.attachment_url)}
        </div>
      )}

      {/* 미션 제출 내용 */}
      {shouldShowMissionSection && (
        <>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-[#424242]">미션 제출하기</h3>
            <div className="flex gap-2 min-h-[32px] items-center">
              {!isMissionEditMode &&
                appliedCampaigns.mission_url &&
                (appliedCampaigns.status === "accepted" ||
                  appliedCampaigns.status === "confirmed") && (
                  <Button
                    size="small"
                    onClick={() => {
                      const existingUrls = appliedCampaigns.mission_url
                        ? appliedCampaigns.mission_url
                            .split(",")
                            .map((url) => url.trim())
                        : [];
                      setTempMissionUrls(existingUrls);
                      setIsMissionEditMode(true);
                      missionForm.setFieldsValue({ mission_url: "" });
                    }}
                  >
                    수정
                  </Button>
                )}
              {(isMissionEditMode || !appliedCampaigns.mission_url) && (
                <>
                  {appliedCampaigns.mission_url && (
                    <Button
                      size="small"
                      onClick={() => {
                        setIsMissionEditMode(false);
                        missionForm.resetFields();
                        setTempMissionUrls([]);
                      }}
                    >
                      취소
                    </Button>
                  )}
                  <Button
                    size="small"
                    type="primary"
                    onClick={handleMissionUpload}
                    disabled={
                      tempMissionUrls.length === 0 ||
                      (selectedCampaign?.seeding_type === "paid" &&
                        appliedCampaigns.status !== "confirmed")
                    }
                  >
                    {appliedCampaigns.mission_url ? "완료" : "제출"}
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="border border-[#e0e0e0] rounded-lg p-4">
            <div className="font-medium text-[#424242] mb-3">안내</div>
            <div className="bg-[#FFF3E0] rounded-lg p-3 mb-4">
              <div className="text-[12px] text-[#E65100] font-medium">
                유가 캠페인의 경우, 먼저 첨부파일(사진/영상)을 제출하고 브랜드의
                승인을 받아야 미션을 제출할 수 있습니다.
              </div>
            </div>
            <Router.MissionDisplay mission={selectedCampaign.mission} />
            <div className="rounded-lg">
              {isMissionEditMode || !appliedCampaigns.mission_url ? (
                <div>
                  <Form
                    form={missionForm}
                    layout="vertical"
                    requiredMark={false}
                    initialValues={{ mission_url: "" }}
                  >
                    <Form.Item
                      name="mission_url"
                      label="미션 URL을 제출하세요."
                      className="mb-0"
                    >
                      <div className="flex gap-2">
                        <Input
                          placeholder="미션 URL만 입력해주세요."
                          onPressEnter={handleAddUrl}
                        />
                        <Button onClick={handleAddUrl}>추가</Button>
                      </div>
                    </Form.Item>
                  </Form>
                  {tempMissionUrls.length > 0 && (
                    <div className="mt-4">
                      {tempMissionUrls.map((url, index) => (
                        <div
                          key={index}
                          className="mb-2 border border-[#e0e0e0] rounded-lg p-2 flex items-center gap-1.5"
                        >
                          🔗
                          <div className="flex-1">
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#1890ff] no-underline break-all cursor-pointer inline"
                            >
                              {url}
                            </a>
                          </div>
                          <Button
                            size="small"
                            type="text"
                            onClick={() => handleRemoveUrl(url)}
                            className="flex-shrink-0 border-none shadow-none p-1 min-w-0 text-[12px] cursor-pointer"
                          >
                            <FontAwesomeIcon
                              icon={faMinus}
                              className="text-red-500"
                            />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {appliedCampaigns.mission_url
                    ?.split(",")
                    .map((url, index) => (
                      <div
                        key={index}
                        className="mb-2 border border-[#e0e0e0] rounded-lg p-2.5 flex items-center gap-1.5"
                      >
                        <span className="leading-[1.5] inline-block">🔗</span>
                        <a
                          href={url.trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1890ff] no-underline break-all leading-[1.5]"
                        >
                          {url}
                        </a>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* 포스팅 제출 내용 (유료 캠페인일 때만) */}
      {appliedCampaigns.status !== "pending" &&
        selectedCampaign?.seeding_type === "paid" && (
          <>
            <div className="flex justify-between items-center mb-3 mt-6">
              <h3 className="text-[#424242]">포스팅 원고 제출하기</h3>
              <div className="flex gap-2 min-h-[32px] items-center">
                {!isPostingEditMode &&
                  appliedCampaigns.status === "accepted" &&
                  appliedCampaigns.confirm_url && (
                    <Button
                      size="small"
                      onClick={() => {
                        setIsPostingEditMode(true);
                        setPostingFiles([]);
                      }}
                    >
                      수정
                    </Button>
                  )}
                {(isPostingEditMode || !appliedCampaigns.confirm_url) && (
                  <>
                    {appliedCampaigns.confirm_url && (
                      <Button
                        size="small"
                        onClick={() => {
                          setIsPostingEditMode(false);
                          setPostingFiles([]);
                        }}
                      >
                        취소
                      </Button>
                    )}
                    <Button
                      size="small"
                      type="primary"
                      onClick={handlePostingUpload}
                      disabled={postingFiles.length === 0 || isPostingUploading}
                      loading={isPostingUploading}
                    >
                      {appliedCampaigns.confirm_url ? "완료" : "제출"}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {appliedCampaigns.feedback && (
              <div className="mb-4 rounded-xl border border-[#ffe1b3] bg-[#fffaf2] px-4 py-3 shadow-[0_8px_24px_rgba(245,158,11,0.08)]">
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-[#92400e]">
                    브랜드 피드백
                  </span>
                </div>
                <p className="whitespace-pre-line break-words text-[14px] leading-[1.65] text-[#5f4217]">
                  {appliedCampaigns.feedback}
                </p>
              </div>
            )}

            <div className="border border-[#e0e0e0] rounded-lg p-4 mb-4">
              <div className="rounded-lg">
                {isPostingEditMode || !appliedCampaigns.confirm_url ? (
                  <div>
                    <Upload
                      beforeUpload={handlePostingFileUpload}
                      onRemove={handleRemovePostingFile}
                      fileList={postingFiles}
                      maxCount={5}
                      accept="image/*,video/*"
                      disabled={isPostingUploading}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        disabled={isPostingUploading}
                      >
                        파일 업로드
                      </Button>
                    </Upload>
                    <div className="text-[12px] text-[#999] mt-2">
                      이미지 또는 비디오 파일만 업로드 가능합니다.
                    </div>
                  </div>
                ) : (
                  <div>
                    {confirmUrls.length > 0 && (
                      <div>
                        <div className="flex justify-between items-center mb-8">
                          <h3 className="text-[15px] font-semibold text-[#424242]">
                            제출한 포스팅
                          </h3>
                          <div className="text-[13px] text-[#666]">
                            <span
                              className={`font-bold ${postingReview.className}`}
                            >
                              {postingReview.label}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {hasMultipleConfirmUrls && (
                            <Button
                              type="text"
                              shape="circle"
                              icon={<LeftOutlined />}
                              onClick={() => postingCarouselRef.current?.prev()}
                              className={
                                isFirstPostingSlide
                                  ? "invisible pointer-events-none"
                                  : ""
                              }
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <Carousel
                              ref={postingCarouselRef}
                              dots={hasMultipleConfirmUrls}
                              infinite={false}
                              afterChange={setPostingCarouselIndex}
                            >
                              {confirmUrls.map((confirmUrl, index) => {
                                const isVideo =
                                  /\.(mp4|mov|avi|webm|ogg)$/i.test(confirmUrl);
                                const isImage =
                                  /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                    confirmUrl
                                  );

                                return (
                                  <div key={`${confirmUrl}-${index}`}>
                                    <div className="relative flex aspect-square items-center justify-center">
                                      {isVideo ? (
                                        <video
                                          src={confirmUrl}
                                          className="max-h-full max-w-full cursor-pointer rounded-md"
                                          controls
                                        />
                                      ) : isImage ? (
                                        <img
                                          src={confirmUrl}
                                          alt={`제출한 포스팅 ${index + 1}`}
                                          className="max-h-full max-w-full cursor-pointer object-contain rounded-md"
                                          onClick={() =>
                                            window.open(
                                              confirmUrl,
                                              "_blank",
                                              "noopener,noreferrer"
                                            )
                                          }
                                        />
                                      ) : null}
                                    </div>
                                  </div>
                                );
                              })}
                            </Carousel>
                          </div>
                          {hasMultipleConfirmUrls && (
                            <Button
                              type="text"
                              shape="circle"
                              icon={<RightOutlined />}
                              onClick={() => postingCarouselRef.current?.next()}
                              className={
                                isLastPostingSlide
                                  ? "invisible pointer-events-none"
                                  : ""
                              }
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
    </>
  );
};
